import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { FilterSubscription } from '@cloudcost/shared-types/reporting';

interface SubscriptionTreePickerProps {
  selected: string[];
  onChange: (ids: string[]) => void;
  testIdPrefix?: string;
}

interface TenantGroup {
  tenantId: number;
  tenantName: string;
  subscriptions: FilterSubscription[];
}

function SubscriptionTreePicker({
  selected,
  onChange,
  testIdPrefix = 'ai-sub',
}: SubscriptionTreePickerProps) {
  const [subscriptions, setSubscriptions] = useState<FilterSubscription[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWrapper.get(urls.DevOps.getFilterSubscriptions);
      const rows = Array.isArray(data) ? (data as FilterSubscription[]) : [];
      setSubscriptions(rows);
      const initialExpanded: Record<number, boolean> = {};
      for (const row of rows) {
        initialExpanded[row.tenant_id] = true;
      }
      setExpanded(initialExpanded);
    } catch {
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const tenants = useMemo<TenantGroup[]>(() => {
    const byTenant = new Map<number, TenantGroup>();
    for (const sub of subscriptions) {
      const existing = byTenant.get(sub.tenant_id);
      if (existing) {
        existing.subscriptions.push(sub);
      } else {
        byTenant.set(sub.tenant_id, {
          tenantId: sub.tenant_id,
          tenantName: sub.tenant_name,
          subscriptions: [sub],
        });
      }
    }
    return [...byTenant.values()].sort((a, b) =>
      a.tenantName.localeCompare(b.tenantName),
    );
  }, [subscriptions]);

  const toggleSubscription = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const toggleTenant = (tenant: TenantGroup) => {
    const ids = tenant.subscriptions.map((s) => s.subscription_id);
    const allSelected = ids.every((id) => selected.includes(id));
    if (allSelected) {
      onChange(selected.filter((id) => !ids.includes(id)));
    } else {
      onChange([...new Set([...selected, ...ids])]);
    }
  };

  const tenantSelectionState = (tenant: TenantGroup): boolean | 'indeterminate' => {
    const ids = tenant.subscriptions.map((s) => s.subscription_id);
    const selectedCount = ids.filter((id) => selected.includes(id)).length;
    if (selectedCount === 0) return false;
    if (selectedCount === ids.length) return true;
    return 'indeterminate';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (tenants.length === 0) {
    return <Alert severity="info">No subscriptions available.</Alert>;
  }

  return (
    <Box
      sx={{
        maxHeight: 320,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
      data-testid={`${testIdPrefix}-tree`}>
      {tenants.map((tenant) => {
        const tenantChecked = tenantSelectionState(tenant);
        return (
          <Accordion
            key={tenant.tenantId}
            expanded={expanded[tenant.tenantId] ?? true}
            onChange={(_e, isExpanded) =>
              setExpanded((prev) => ({
                ...prev,
                [tenant.tenantId]: isExpanded,
              }))
            }
            disableGutters
            elevation={0}
            sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <FormControlLabel
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                control={
                  <Checkbox
                    size="small"
                    checked={tenantChecked === true}
                    indeterminate={tenantChecked === 'indeterminate'}
                    onChange={() => toggleTenant(tenant)}
                    data-testid={`${testIdPrefix}-tenant-${tenant.tenantId}`}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {tenant.tenantName}
                  </Typography>
                }
              />
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pl: 4 }}>
              {tenant.subscriptions.map((sub) => (
                <FormControlLabel
                  key={sub.subscription_id}
                  sx={{ display: 'flex', ml: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(sub.subscription_id)}
                      onChange={() => toggleSubscription(sub.subscription_id)}
                      data-testid={`${testIdPrefix}-${sub.subscription_id}`}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {sub.display_name || 'Unnamed subscription'}
                    </Typography>
                  }
                />
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

export default SubscriptionTreePicker;
